// src/cockpit4/components/KpiCard.tsx
// ============================================
// 增强版 KPI 卡片（P0-功能4）- v2.0 详细改进计划
//
// 功能：
//   1. 数值展示（大号 monospaced 字体）
//   2. 迷你趋势图（sparkline，仅当 trend 数据存在时展示）
//   3. 同比/环比百分比（上升绿/下降红）
//   4. 目标完成率进度条（仅当 target 存在时展示）
//   5. 异常变化高亮（上升绿/下降红/持平灰）
//   6. 点击卡片下钻（onClick 回调）
//   7. 数据降级标记（degraded=true 时显示警告）
//
// 数据来源：
//   - useDashboardData 的 CEO KPI（revenue/profit/cash/newCustomers 等）
//   - revenueTrend（如有，用于 sparkline）
//   - 调用方传入 target/previousValue/delta
//
// 设计原则：
//   - 真实数据驱动：仅在数据存在时展示对应区块，不生成假数据
//   - 性能优先：sparkline 用 SVG 直接绘制（不引入额外图表库）
// ============================================

import { memo, useMemo } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  AlertCircle,
  type LucideIcon,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

export interface KpiCardProps {
  /** 卡片标签（如"今日收入"） */
  label: string;
  /** 英文副标题（如"Revenue"） */
  subtitle?: string;
  /** 当前值（已格式化的字符串，如"¥ 1,234"或"5 篇"） */
  value: string | number;
  /** 图标 */
  icon: LucideIcon;
  /** 主题色（hex，如 #10B981） */
  color: string;
  /** 数值变化百分比（正数上升、负数下降、0 持平；undefined 表示无数据） */
  deltaPercent?: number;
  /** 变化类型标签（如"环比" / "同比"） */
  deltaLabel?: string;
  /** 目标值（用于进度条） */
  target?: number;
  /** 当前实际数值（用于计算完成率；如 revenue=1234） */
  actual?: number;
  /** 完成率单位（% 或 篇 等显示用） */
  progressUnit?: string;
  /** 趋势数据（sparkline 点序列，每项 0-100 归一化或原始数值） */
  trend?: number[];
  /** 数据是否降级（降级时显示警告角标） */
  degraded?: boolean;
  /** v6.3.24 数据来源说明（真实状态/降级原因，悬浮显示完整文案） */
  dataSource?: string;
  /** 点击回调（下钻） */
  onClick?: () => void;
  /** 入场动画延迟（秒） */
  animationDelay?: number;
}

// ============================================
// Sparkline 迷你趋势图（SVG 直接绘制，无依赖）
// ============================================

interface SparklineProps {
  data: number[];
  color: string;
  width?: number;
  height?: number;
}

function Sparkline({ data, color, width = 80, height = 24 }: SparklineProps) {
  const path = useMemo(() => {
    if (!data || data.length < 2) return "";
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const stepX = width / (data.length - 1);
    return data
      .map((v, i) => {
        const x = i * stepX;
        const y = height - ((v - min) / range) * height;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  }, [data, width, height]);

  const areaPath = useMemo(() => {
    if (!path || data.length < 2) return "";
    const lastX = (data.length - 1) * (width / (data.length - 1));
    return `${path} L${lastX.toFixed(1)},${height} L0,${height} Z`;
  }, [path, data, width, height]);

  if (!data || data.length < 2) return null;

  const gradientId = `sparkline-grad-${color.replace("#", "")}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* 末点高亮 */}
      {data.length > 0 && (
        <circle
          cx={(data.length - 1) * (width / (data.length - 1))}
          cy={height - ((data[data.length - 1] - Math.min(...data)) / (Math.max(...data) - Math.min(...data) || 1)) * height}
          r={1.8}
          fill={color}
        />
      )}
    </svg>
  );
}

// ============================================
// 主组件
// ============================================

function KpiCardImpl({
  label,
  subtitle,
  value,
  icon: Icon,
  color,
  deltaPercent,
  deltaLabel = "环比",
  target,
  actual,
  progressUnit = "%",
  trend,
  degraded = false,
  dataSource,
  onClick,
  animationDelay = 0,
}: KpiCardProps) {
  // 完成率计算
  const progress = useMemo(() => {
    if (target === undefined || actual === undefined || target === 0) return null;
    return Math.min(100, Math.round((actual / target) * 100));
  }, [target, actual]);

  // 完成率数值（用于显示，如"65%"或"1,234/2,000"）
  const progressText = useMemo(() => {
    if (progress === null) return null;
    if (target !== undefined && actual !== undefined) {
      return `${progress}${progressUnit}（${actual}/${target}）`;
    }
    return `${progress}${progressUnit}`;
  }, [progress, target, actual, progressUnit]);

  // delta 状态
  const deltaStatus: "up" | "down" | "flat" | "none" =
    deltaPercent === undefined
      ? "none"
      : deltaPercent > 0
      ? "up"
      : deltaPercent < 0
      ? "down"
      : "flat";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: animationDelay, ease: "easeOut" }}
      onClick={onClick}
      className={`relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-4 overflow-hidden transition-all ${
        onClick ? "cursor-pointer hover:border-white/10 hover:bg-white/[0.04]" : ""
      }`}
    >
      {/* 顶部光带 */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }}
      />
      {/* 角落光晕 */}
      <div
        className="absolute -top-8 -right-8 w-20 h-20 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          filter: "blur(20px)",
        }}
      />

      {/* 降级警告角标 */}
      {degraded && (
        <div
          className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-amber-500/15 text-amber-400 border border-amber-500/30"
          title={dataSource || "数据源未连接，使用降级数据"}
        >
          <AlertCircle size={8} />
          降级
        </div>
      )}

      {/* Header：图标 + 副标题 */}
      <div className="flex items-center justify-between mb-2 relative">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={14} style={{ color }} />
        </div>
        {subtitle && (
          <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">
            {subtitle}
          </span>
        )}
      </div>

      {/* 主数值 */}
      <div className="text-[22px] font-mono font-bold text-white tracking-tight leading-none">
        {value}
      </div>

      {/* 标签 + delta */}
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
        {deltaStatus !== "none" && (
          <span
            className={`flex items-center gap-0.5 text-[10px] font-mono ${
              deltaStatus === "up"
                ? "text-emerald-400"
                : deltaStatus === "down"
                ? "text-red-400"
                : "text-gray-500"
            }`}
            title={deltaLabel}
          >
            {deltaStatus === "up" ? (
              <ArrowUpRight size={11} />
            ) : deltaStatus === "down" ? (
              <ArrowDownRight size={11} />
            ) : (
              <Minus size={11} />
            )}
            {Math.abs(deltaPercent || 0)}%
            <span className="text-[8px] text-gray-600 ml-0.5">{deltaLabel}</span>
          </span>
        )}
      </div>

      {/* Sparkline 趋势图（仅当有数据时展示） */}
      {trend && trend.length >= 2 && (
        <div className="mt-2 flex items-end justify-end">
          <Sparkline data={trend} color={color} width={100} height={28} />
        </div>
      )}

      {/* 目标完成进度条（仅当有 target 和 actual 时展示） */}
      {progress !== null && progressText && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">
              目标完成
            </span>
            <span className="text-[9px] font-mono text-gray-400">{progressText}</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, delay: animationDelay + 0.2, ease: "easeOut" }}
              className="h-full rounded-full relative"
              style={{
                background: `linear-gradient(90deg, ${color}, ${color}cc)`,
              }}
            >
              {/* 进度条末端高亮 */}
              {progress >= 100 && (
                <span
                  className="absolute right-0 top-0 bottom-0 w-1 rounded-full"
                  style={{ background: "#fff", boxShadow: `0 0 6px ${color}` }}
                />
              )}
            </motion.div>
          </div>
        </div>
      )}

      {/* v6.3.24 数据来源说明（真实状态/降级原因，单行截断，悬浮看全文）
          V1.5 P0-5：提亮对比度（text-gray-600 → text-gray-400，尺寸 8px → 9px） */}
      {dataSource && (
        <div
          className="mt-2 text-[9px] font-mono text-gray-400 truncate leading-tight"
          title={dataSource}
        >
          {dataSource}
        </div>
      )}
    </motion.div>
  );
}

// 导出 Sparkline 以便其他组件复用
export { Sparkline };

// 使用 memo 优化重渲染
export const KpiCard = memo(KpiCardImpl);
export default KpiCard;
